import type { PermissionScopeTypeEnum } from '@/types/composition-functions';
import { useGetPermissionScopeOptionsQuery } from '@/types/vue-apollo-helpers';

export interface ScopeValueOption {
  label: string;
  value: string;
}

/**
 * Selectable values for the permission-scope dimensions, for the scope pickers
 * on the role-request and grant dialogs.
 *
 * These pickers used to read the management lists (`listSchoolProgrammes`,
 * `listSchoolDepartments`, …), which are permission-scoped: a staff member
 * holding no grant yet resolves to zero rows there, so every dropdown came up
 * empty and a scoped role could not be requested without already holding one.
 * `permissionScopeOptions` is the catalogue endpoint for exactly this case.
 *
 * One request covers every dimension the school offers, so callers need no
 * per-type lazy loading — pass the types the picker lists and read them off.
 */
export function usePermissionScopeOptions(
  scopeTypes: MaybeRefOrGetter<PermissionScopeTypeEnum[]>,
) {
  const { result, loading, error } = useGetPermissionScopeOptionsQuery(
    () => ({ scope_types: toValue(scopeTypes) }),
    () => ({
      enabled: toValue(scopeTypes).length > 0,
      fetchPolicy: 'cache-and-network',
    }),
  );

  const optionsByType = computed<Record<string, ScopeValueOption[]>>(() =>
    Object.fromEntries(
      (result.value?.permissionScopeOptions ?? []).map((group) => [
        group.scope_type,
        group.options.map((o) => ({ label: o.label, value: o.value })),
      ]),
    ),
  );

  const scopeValueOptionsFor = (type: PermissionScopeTypeEnum | null): ScopeValueOption[] =>
    type ? optionsByType.value[type] ?? [] : [];

  return {
    scopeValueOptionsFor,
    loadingScopeOptions: loading,
    scopeOptionsError: error,
  };
}
